import * as Tooltip from '@radix-ui/react-tooltip';
import { ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

import { SUPER_ADMIN } from '@/features/auth/roles';
import { FOOTER_NAV_ITEMS, NAV_ITEMS } from '@/app/navigation/navItems';
import { holdsProductFor } from '@/app/navigation/productAccess';
import type { NavItem } from '@/app/navigation/navItems';
import { ROUTES } from '@/config/routes';
import { brandMarkFor } from '@/shared/brand/logos';
import { cn } from '@/shared/lib/cn';
import { useTheme } from '@/components/ThemeProvider';
import { isRouteActive } from '@/app/navigation/activeRoute';

import { SidebarNavRow } from './SidebarNavRow';
import { SidebarUserProfile } from './SidebarUserProfile';
import type { SidebarUser } from './SidebarUserProfile';

export interface AppSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  /**
   * The role keys this session holds, from `/account/me`.
   *
   * Empty while the request is in flight, which hides every restricted row for
   * that moment. The opposite default — show everything until told otherwise —
   * would flash the Team row at a team member on every page load.
   */
  roleKeys: ReadonlySet<string>;

  /**
   * The product keys this session holds, from the same place.
   *
   * <p>A separate prop rather than being read in here, for the reason the roles are: the
   * layout asks `/account/me` once and hands both down, so the rail and the top bar cannot
   * end up answering from two different reads of the same session.
   *
   * <p>A `Set` rather than an array because every row asks it the same question, and a row
   * declaring two products asks twice.
   */
  productKeys: ReadonlySet<string>;
  /**
   * The signed-in person, for the profile block at the foot of the rail.
   *
   * <p>Passed in rather than read from context, like `roleKeys` and `productKeys` above and
   * for the same reason: this component is a function of its arguments and renders in a test
   * without a provider. Optional, so a caller that has no session yet simply omits it.
   */
  user?: SidebarUser | null;
}

/**
 * Whether a row is shown at all.
 *
 * <h2>Two independent gates, and a row must clear both</h2>
 *
 * `roleKeys` asks what kind of user this is; `productKeys` asks what they have been
 * assigned. They are different questions with different answers, and a row declares
 * whichever of them applies to it — most declare neither.
 *
 * | Row | Declares | Shown to |
 * |---|---|---|
 * | Dashboard, My Account | neither | everybody signed in |
 * | Team | `roleKeys` | a primary admin or an admin |
 * | Email Warmup, Brand Intelligence, … | `productKeys` | whoever holds that product |
 * | Email Warmup (receivers), Review Management | `roleKeys: ['super-admin']` | the platform operator |
 *
 * <p>Nothing declares both today and nothing stops one doing so; the gates are written
 * independently so that the day a row needs both, it says so and works.
 *
 * <h2>Products are matched ANY-of, not all-of</h2>
 *
 * "Xtremand AI Agent" fronts `seo_ai_agent` and `linkedin_ai_agent`, and holding either is
 * enough to want the row — it is a picker standing in front of two agents. All-of would hide
 * a product somebody has paid for because they have not paid for its neighbour.
 *
 * <p>The cost lands one level down and is worth knowing: that screen must then show only the
 * agent they hold, or they arrive at a setup form for the one they do not.
 *
 * <h2>Absent answers hide, rather than show</h2>
 *
 * Both sets are empty while `/account/me` is in flight. Erring towards hidden means nobody
 * sees a row flash up and disappear; erring the other way shows every product to everybody
 * for a moment on every page load, which is the worse of the two.
 *
 * <p>In practice that moment does not arrive: `ProtectedRoute` renders a loader until the
 * session resolves, so this component is not mounted with an unanswered `/account/me`. The
 * default is belt-and-braces rather than a live concern — which is the right way round, since
 * the failure it guards against is silent.
 *
 * <h2>The platform operator sees only their own rows, and the rule is inverted</h2>
 *
 * For everybody else a row with no `roleKeys` is a row for everybody. For the super admin it
 * is the opposite: an unmarked row is a TENANT row, and they get none of them — so the
 * product gate below never runs for them, which is correct rather than an oversight. They
 * hold no `user_privilege_product` rows at all: those belong to a customer's company, and
 * the operator belongs to the platform's.
 *
 * <p>That asymmetry is the point. The alternative was naming every tenant role on all seven
 * product rows, which works today and fails on the eighth — the next product row will not
 * declare `roleKeys`, will reappear in the operator's menu, and nothing will report it.
 * Inverting asks the question that actually decides the answer, "is this row the platform's
 * or a tenant's", and a new tenant row is invisible to the operator without anybody
 * remembering.
 *
 * <h2>A product gate LOCKS the row; a role gate hides it</h2>
 *
 * Both gates hid the row until 8 October 2026, and the source does neither thing uniformly:
 * `AppNav.tsx` holds `emailWarmupLocked`, `reviewsLocked` and `developerToolsLocked`, all
 * three initially true, and draws a padlock on those rows rather than removing them. Those
 * three are exactly our product-gated rows.
 *
 * <p>So the two gates now answer differently, because they are answering different
 * questions. A product is something somebody can come to hold — by buying it, or by an
 * administrator assigning it — so a padlock tells them the screen is there and why it is
 * shut, and `ProductNotAssigned` says the same thing again if they click. **A role is not**:
 * a team member cannot acquire an administrator role by clicking, so a padlocked Team row
 * would only advertise something they can never have. Team and the operator's own rows
 * stay hidden.
 *
 * <p>This function therefore answers the role question alone and {@link isLocked} answers
 * the product one. Children are the one exception and are still **hidden** by product: a
 * child names one agent where the parent names both, and the source has no lock on a
 * sub-item at all — a locked parent has no children to offer.
 *
 * <h2>Neither is a boundary</h2>
 *
 * The row is hidden or marked; the route is not closed. Anyone can type `/email-warmup`, and
 * every endpoint behind it carries `@RequiresProduct` and answers 403 `product-not-assigned`
 * on its own. This function and the padlock only decide what somebody is shown.
 */
function isVisible(item: NavItem, roleKeys: ReadonlySet<string>): boolean {
  if (roleKeys.has(SUPER_ADMIN)) {
    return item.roleKeys?.includes(SUPER_ADMIN) ?? false;
  }

  return !item.roleKeys?.length || item.roleKeys.some((key) => roleKeys.has(key));
}

/**
 * Whether to draw the padlock on a row.
 *
 * <p>Asks {@link holdsProductFor} — the same function `ProductRoute` asks, deliberately.
 * Written twice, the padlock and the screen behind it drift apart, and the way they drift is
 * the worst of the available bugs here: a row that offers no warning and then refuses, or one
 * marked shut in front of a screen that works.
 *
 * <p>A row naming no product is never locked. Home and Team are backed by nothing sold,
 * so there is nothing for a lock to be about.
 *
 * <p><b>Err towards unlocked while the answer is in flight.</b> `productKeys` is empty until
 * `/account/me` answers, and assuming locked would padlock every product row for a moment on
 * every page load — which reads as "you have bought nothing" to somebody who has. In practice
 * the moment does not arrive, since `ProtectedRoute` renders a loader until the session
 * resolves; the direction is belt-and-braces. It is the opposite of the default `isVisible`
 * takes, and the two do not contradict each other: a row that appears and vanishes is worse
 * than one that appears a moment late, and a lock that appears and vanishes is worse than one
 * that appears a moment late.
 */
function isLocked(item: NavItem, productKeys: ReadonlySet<string>): boolean {
  return !holdsProductFor(item, productKeys);
}

/**
 * The left-hand menu.
 *
 * <h2>Its lengths are the source's, read off the file rather than the screenshot</h2>
 *
 * **`w-65` wide**, collapsing to `w-17.5`; `h-16 px-5` header
 * with the mark at `max-w-[88%]`; `px-3 py-4` around the rows; and one `flex-col` holding the
 * scroll area and the three pinned rows, so `mt-3 space-y-1` is the whole gap between the
 * last menu row and the assistant.
 *
 * <p><b>Those are rem, and the root font here is 14px.</b> `--spacing` is `0.25rem` and
 * `index.css` sets `html { font-size: 14px }`, so `w-65` is 16.25rem = **227.5px** and
 * `w-17.5` is 61.25px — not the 260 and 70 a 16px root would give. The rail was written with
 * those two numbers as absolute pixels on 8 October 2026 and came out 32.5px too wide, which
 * is the second thing reported as "a lot of space"; it was 314px and 54px too wide before
 * that, having been built from the design rather than from the file.
 *
 * <p>So a length copied from the design is copied as the CLASS, not as the pixels the class
 * happens to resolve to on a 16px root. Every other length here is already a Tailwind
 * spacing utility and was therefore right from the first pass.
 *
 * <h2>The assistant is a row, not a card</h2>
 *
 * A bordered gradient panel with a pulsing dot, a status line and a 32px tile — "XTREMAND
 * ASSISTANT / Ready to assist" — stood here for a day. The source has a single row the height
 * of a menu row: a filled sparkle, the words, and a chevron, on a tinted ground that is the
 * only thing marking it out from the rows above. Three pinned rows of one height read as a
 * group; one card and two rows read as an advertisement above a menu.
 *
 * <h2>NavLink, not a button and a callback</h2>
 *
 * Each row is a real link with a real `href`, so middle-click opens a tab,
 * ctrl-click works, and the active state comes from the router rather than from
 * a piece of state something has to remember to update.
 */
export function AppSidebar({
  collapsed,
  onToggle,
  roleKeys,
  productKeys,
  user = null,
}: AppSidebarProps) {
  // Read once. The menu and the assistant row ask the same question, and asking it
  // twice is how the two stop agreeing.
  const isOperator = roleKeys.has(SUPER_ADMIN);

  // Settings & help is a plain Link rather than a NavLink, so its active state is read here
  // -- one address compared once, where a NavLink's render prop would be a second way of
  // answering the same question a few lines from the rows that use the first.
  const { pathname } = useLocation();

  const { actualTheme } = useTheme();

  /*
   * Two files, because the wordmark is ink rather than a shape: "XTREMAND ENRICH
   * & ENGAGE" is set in the same dark navy as the dark theme's own background, so
   * on dark only the X was visible and the words vanished. The mark itself is a
   * gradient that reads on either ground, which is why the fault looked like a
   * half-rendered image rather than a missing asset.
   */
  const logo = brandMarkFor(actualTheme);

  return (
    <div
      className={cn(
        'fixed top-0 left-0 z-40 hidden h-screen transition-all duration-500 ease-out lg:flex lg:flex-col',
        // `w-65` and `w-17.5`, the design's own classes, NOT `w-[260px]` and `w-[70px]`.
        // Tailwind's spacing scale is rem-based and this application's root font is 14px --
        // see the note above -- so these are 227.5px and 61.25px. Writing them as absolute
        // pixels on 8 October 2026 made the rail 32.5px too wide, which is what the second
        // report of "a lot of space" was.
        collapsed ? 'w-17.5' : 'w-65',
      )}
    >
      {/*
        Opaque white, not `bg-white/80 backdrop-blur-xl`, which is what it was until
        8 October 2026: the page behind it is `#f8fafc`, so 80% white over it rendered the
        rail a shade cooler than the header sitting inside it and a shade cooler than the
        design. Nothing scrolls under a `fixed` rail, so the blur had nothing to blur.
      */}
      <div className="flex min-h-0 flex-1 flex-col border-r border-gray-200 bg-white dark:border-white/10 dark:bg-[#0c182d]">
        {/* Header: the mark, and the control that collapses the rail. */}
        <div
          className={cn(
            'flex h-16 flex-shrink-0 items-center border-b border-gray-200 bg-white dark:border-white/10 dark:bg-[#0c182d]',
            collapsed ? 'flex-col justify-center gap-1 px-2' : 'justify-between px-5',
          )}
        >
          {/*
            A bare image, as the design has it, and NOT wrapped in a `Link`.

            `max-w-[88%]` is a percentage of the flex container, so as a direct child of this
            `justify-between` header it is 88% of the header's content box. Wrapped in a
            `Link flex min-w-0 items-center` -- which is what this was until 8 October 2026 --
            the percentage resolves against the LINK, and `min-w-0` then lets the link shrink
            to the image's own width. The mark came out 18% smaller and the collapse control
            12px further left, which is the whole of what was left of the top strip's
            difference.

            So it is an image and not a link to the dashboard. The design does not make it
            one, Home is the first row of the menu directly below it, and a wrapper here is
            what broke the only length this header has.
          */}
          {collapsed ? null : <img src={logo} alt="XTREMAND" className="max-w-[88%]" />}

          {/*
            The design's tooltip on this control — `AppNav.tsx:1453-1470` wraps the same
            button in a Radix tooltip saying exactly what the `aria-label` says.

            `delayDuration={0}` because the design's primitive defaults to 0, so the label
            appears the instant the pointer lands rather than after the 300ms the Team
            screen uses. A rail control is hovered in passing on the way to a menu row; a
            delay there reads as the tooltip not working.

            The PROVIDER is local, as it is in the design. `AppSidebar` is rendered by
            `AppLayout` on every signed-in route, so one here covers the whole application
            without putting a provider at the composition root for a single tooltip.
          */}
          <Tooltip.Provider delayDuration={0}>
            <Tooltip.Root>
              <Tooltip.Trigger asChild>
                <button
                  type="button"
                  onClick={onToggle}
                  aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                  /*
                   * `sidebar-toggle-button` is the design's and is load-bearing on the dark
                   * theme: `index.css` repaints every `<button>` not on a fourteen-name
                   * exclusion list with `!important`, and this is one of the fourteen. Without
                   * it this control is the generic skin button rather than the design's.
                   */
                  className="sidebar-toggle-button flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl p-2 text-gray-500 transition-colors duration-200 hover:bg-gray-100 hover:text-gray-700 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-200"
                >
                  {/*
              `size-4`, not the design's literal `h-5 w-5`: this control is a shadcn `Button`
              there, whose base rule forces every icon inside it to `size-4` at a specificity
              the icon's own class cannot reach. 14px is what the design renders. The rail's
              ROWS are plain links with no such rule, so their `h-5 w-5` is real and stays.
              `AppTopBar` has the compiled selector.
            */}
                  {collapsed ? (
                    <ChevronRight className="size-4" />
                  ) : (
                    <ChevronLeft className="size-4" />
                  )}
                </button>
              </Tooltip.Trigger>

              <Tooltip.Portal>
                <Tooltip.Content
                  side="right"
                  sideOffset={6}
                  collisionPadding={8}
                  className="z-50 w-fit rounded-md bg-gray-900 px-3 py-1.5 text-xs text-white shadow-lg dark:bg-gray-700"
                >
                  {collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                  {/*
                    `fill-gray-900`, matching the body, where the design's primitive uses
                    `fill-primary`. That variable is `#030213` on the light theme and
                    `#fafafa` on the dark one, so the design draws a WHITE arrow on its own
                    dark tooltip. Copying the class would copy the defect.
                  */}
                  <Tooltip.Arrow className="fill-gray-900 dark:fill-gray-700" />
                </Tooltip.Content>
              </Tooltip.Portal>
            </Tooltip.Root>
          </Tooltip.Provider>
        </div>

        {/*
          The rows and the three pinned ones, in ONE padded column.

          The source's shape, and the reason the gap the rail was reported for is gone: the
          scroll area is `flex-1` and the pinned block is `mt-3`, so when the menu is short
          the empty space is INSIDE the scroll area and the pinned rows sit 12px under the
          last menu row. Two separately padded siblings -- which is what this was -- give the
          scroll area the whole height and push the footer to the bottom of the screen,
          leaving a hole whose size is however many products the person holds.
        */}
        <div className={cn('flex min-h-0 flex-1 flex-col py-4', collapsed ? 'px-2' : 'px-3')}>
          <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
            {/*
              The group heading. Hidden when collapsed -- "WORKSPACE" over a 70px rail of
              icons labels nothing, and the rail has only one group.
            */}
            {collapsed ? null : (
              <p className="mb-2 px-3 text-[11px] font-semibold tracking-wider text-gray-500 uppercase">
                Workspace
              </p>
            )}

            <nav className="space-y-1" aria-label="Main">
              {NAV_ITEMS.filter((item) => isVisible(item, roleKeys)).map((item) => (
                <SidebarNavRow
                  key={item.id}
                  item={item}
                  collapsed={collapsed}
                  locked={isLocked(item, productKeys)}
                  /*
                   * Filtered here, with the session in hand. A child names one agent where
                   * the parent names both, so somebody holding only the SEO agent sees the
                   * parent and one child -- which is the whole reason the parent's gate is
                   * any-of. Hidden rather than locked: see `isVisible`.
                   */
                  visibleChildren={(item.children ?? []).filter(
                    (child) => isVisible(child, roleKeys) && !isLocked(child, productKeys),
                  )}
                />
              ))}
            </nav>
          </div>

          {/* The assistant, Settings & help, and the signed-in person. */}
          <div className="mt-3 flex-shrink-0 space-y-1">
            {/*
              Hidden from the platform operator for the same reason every product row is: the
              assistant is a tenant feature, and `ROUTES.aiAssistant` is one of the screens
              they have no business in.
            */}
            {isOperator ? null : (
              <Link
                to={ROUTES.aiAssistant}
                title="Xtremand Assistant"
                className={cn(
                  'group flex items-center rounded-xl border border-blue-100 bg-blue-50/60 text-blue-600 transition-colors hover:bg-blue-50 focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none dark:border-blue-400/20 dark:bg-blue-500/10 dark:text-blue-300',
                  collapsed ? 'justify-center p-3' : 'gap-3 px-3 py-3',
                )}
              >
                {/* `fill-current` -- the source's filled sparkle, not the outline one. */}
                <Sparkles aria-hidden className="h-5 w-5 flex-shrink-0 fill-current" />
                {collapsed ? null : (
                  <>
                    <span className="flex-1 truncate text-sm font-semibold">
                      Xtremand Assistant
                    </span>
                    <ChevronRight aria-hidden className="h-4 w-4 flex-shrink-0" />
                  </>
                )}
              </Link>
            )}

            {/*
              Settings & help opens `/account`, whose own description is "Profile & settings"
              -- there is no separate settings screen and this redesign did not invent one.

              Not a `SidebarNavRow`: it ends in a right chevron rather than an expander and
              carries no padlock, so sharing that component would mean two branches in it for
              one row. `FOOTER_NAV_ITEMS` still supplies the route and the icon, so the row
              and the screen cannot disagree about where it goes.
            */}
            {FOOTER_NAV_ITEMS.filter((item) => isVisible(item, roleKeys)).map((item) => {
              const Icon = item.icon;
              const current = isRouteActive(item.route, pathname);

              return (
                <Link
                  key={item.id}
                  to={item.route}
                  title={item.label}
                  className={cn(
                    'group flex items-center rounded-xl text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none',
                    collapsed ? 'justify-center p-3' : 'gap-3 px-3 py-2.5',
                    current
                      ? 'bg-blue-50 text-blue-600 dark:bg-blue-500/15 dark:text-blue-300'
                      : 'text-gray-900 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-white/5',
                  )}
                >
                  <Icon aria-hidden className="h-5 w-5 flex-shrink-0" />
                  {collapsed ? null : (
                    <>
                      <span className="flex-1 truncate">{item.label}</span>
                      <ChevronRight aria-hidden className="h-4 w-4 flex-shrink-0 text-gray-400" />
                    </>
                  )}
                </Link>
              );
            })}

            <SidebarUserProfile collapsed={collapsed} user={user} />
          </div>
        </div>
      </div>
    </div>
  );
}
